/**
 * TRK-01 (Doom's R1 review): under the account's "Match position", each bot's OWN position on this game — Penelope
 * only, and the volatility harvester only — in the same presentation, so the aggregate stays the one figure for the
 * game and the bot's part of it is readable on its own. The relay attributes fills to a bot by its audited client
 * order id root (relay/src/services/penelope/strategyPositions.ts); a bot with no fills on the game shows nothing.
 * Polled only while mounted, only for a member Penelope is on for, only on the series she runs on.
 */
import { useCallback, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { useVisibilityAwarePoll } from '@/hooks/useVisibilityAwarePoll';
import { penelopeEventTicker } from '@/hooks/usePenelope';
import { usePenelopeAccess } from '@/hooks/usePenelopeAccess';
import { PENELOPE_EVENT_PREFIXES } from '@/hooks/usePenelopeSides';
import type { PairPositionRow } from '@/lib/trading/pairSummary';
import { PairContextCard } from './PairContextCard';
import type { GameRow } from './types';

/** Every 5 s while a bot has fills on the game; every 30 s while neither has any (most games), which keeps the
 *  dashboard rail from reading two fill folds a second across the desk for nothing. */
const POLL_MS = 5_000;
const POLL_IDLE_MS = 30_000;

export interface StrategyBucket {
  root: 'pen' | 'pvh';
  label: string;
  fills: number;
  contracts: number;
  positions: PairPositionRow[];
  realizedPnlCents: number;
  feesCents: number | null;
  firstFillAt: string | null;
  lastFillAt: string | null;
}

const usd = (cents: number): string => `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;

export function StrategyPositionCards({ game }: { game: GameRow }) {
  const access = usePenelopeAccess();
  const event = penelopeEventTicker(game);
  const supported = event !== null && PENELOPE_EVENT_PREFIXES.some((p) => event.startsWith(p));
  const [buckets, setBuckets] = useState<StrategyBucket[]>([]);
  // A relay without the route (an older build behind a newer bundle) answers 404: stop asking, rather than every 5 s.
  const [unsupported, setUnsupported] = useState(false);
  const read = useCallback(
    async (signal: AbortSignal) => {
      if (!event) return;
      try {
        const res = await apiFetch(`/api/penelope/strategy-positions?eventTicker=${encodeURIComponent(event)}`);
        if (signal.aborted) return;
        if (res.status === 404) { setUnsupported(true); return; }
        if (!res.ok) return;
        const body = (await res.json().catch(() => null)) as { buckets?: StrategyBucket[] } | null;
        if (!signal.aborted && Array.isArray(body?.buckets)) setBuckets(body.buckets);
      } catch {
        /* the aggregate above is the authority; a missed read just keeps the last view */
      }
    },
    [event],
  );
  const anyFills = buckets.some((b) => b.fills > 0);
  useVisibilityAwarePoll(read, anyFills ? POLL_MS : POLL_IDLE_MS, { enabled: access && supported && !unsupported });
  if (!access || !supported) return null;
  const shown = buckets.filter((b) => b.fills > 0);
  if (shown.length === 0) return null;
  return (
    <div data-testid="strategy-position-cards">
      {shown.map((b) => (
        <PairContextCard
          key={b.root}
          game={game}
          rows={b.positions}
          title={`${b.label} only (K)`}
          testId={`strategy-position-${b.root}`}
          extra={`${b.fills} fill${b.fills === 1 ? '' : 's'} · ${b.contracts} contracts · realized ${usd(b.realizedPnlCents)}${b.feesCents == null ? '' : ` · fees ${usd(b.feesCents)}`}`}
        />
      ))}
    </div>
  );
}
